<!-- eslint-disable -->
<template>
  <v-card elevation="2">
    <v-card-text class="pa-6">
      <v-form @submit.prevent="saveProfile" ref="formRef">
        <!-- Avatar Upload -->
        <div class="mb-6 text-center">
          <v-avatar size="120" color="primary" class="mb-4">
            <v-img v-if="avatarPreview || profileData.avatarUrl" :src="avatarPreview || profileData.avatarUrl"></v-img>
            <span v-else class="text-h2">{{ profileData.displayName?.charAt(0) || 'U' }}</span>
          </v-avatar>
          
          <div>
            <v-file-input
              v-model="avatarFile"
              accept="image/*"
              :label="$t('profile.uploadAvatar')"
              prepend-icon="mdi-camera"
              variant="outlined"
              hide-details
              @change="previewAvatar"
              class="mb-2"
              style="max-width: 400px; margin: 0 auto;"
            ></v-file-input>
            
            <v-btn
              v-if="avatarFile || profileData.avatarUrl"
              variant="text"
              color="error"
              size="small"
              @click="removeAvatar"
            >
              {{ $t('profile.removeAvatar') }}
            </v-btn>
          </div>
        </div>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="profileData.displayName"
              :label="$t('auth.name')"
              variant="outlined"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="profileData.username"
              label="Username"
              variant="outlined"
              disabled
              hint="Username cannot be changed"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="profileData.bio"
              :label="$t('profile.bio')"
              :placeholder="$t('profile.bioPlaceholder')"
              variant="outlined"
              rows="4"
              counter="500"
              :rules="[rules.maxLength]"
            ></v-textarea>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="profileData.location"
              :label="$t('profile.location')"
              variant="outlined"
              prepend-inner-icon="mdi-map-marker"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="profileData.website"
              :label="$t('profile.website')"
              variant="outlined"
              prepend-inner-icon="mdi-web"
              placeholder="https://..."
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="profileData.birthday"
              :label="$t('profile.birthday')"
              variant="outlined"
              type="date"
              prepend-inner-icon="mdi-cake-variant"
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="profileData.interests"
              :label="$t('profile.interests')"
              :placeholder="$t('profile.interestsPlaceholder')"
              variant="outlined"
              rows="3"
            ></v-textarea>
          </v-col>

          <!-- Privacy Settings -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">{{ $t('profile.privacy') }}</h3>
          </v-col>

          <v-col cols="12" md="6">
            <v-checkbox
              v-model="profileData.showRealName"
              :label="$t('profile.showRealName')"
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols="12" md="6">
            <v-checkbox
              v-model="profileData.showEmail"
              :label="$t('profile.showEmail')"
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols="12" md="6">
            <v-checkbox
              v-model="profileData.emailNotifications"
              :label="$t('profile.emailNotifications')"
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols="12" md="6">
            <v-checkbox
              v-model="profileData.showOnlineStatus"
              :label="$t('profile.showOnlineStatus')"
              hide-details
            ></v-checkbox>
            <p class="text-caption text-grey ml-8">{{ $t('profile.showOnlineStatusHint') }}</p>
          </v-col>
        </v-row>

        <!-- Success/Error Messages -->
        <v-alert v-if="successMessage" type="success" class="mt-4" closable @click:close="successMessage = ''">
          {{ successMessage }}
        </v-alert>
        <v-alert v-if="errorMessage" type="error" class="mt-4" closable @click:close="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>

        <!-- Actions -->
        <div class="d-flex mt-6" style="gap: 1rem;">
          <v-btn
            type="submit"
            color="primary"
            size="large"
            :loading="saving"
          >
            {{ $t('profile.saveChanges') }}
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            @click="resetForm"
          >
            {{ $t('profile.cancelChanges') }}
          </v-btn>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'

const { t } = useI18n()

const props = defineProps({
  profile: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['updated'])

const formRef = ref(null)
const saving = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const avatarFile = ref(null)
const avatarPreview = ref(null)

const profileData = reactive({
  userId: 0,
  displayName: '',
  username: '',
  bio: '',
  location: '',
  website: '',
  birthday: '',
  interests: '',
  showRealName: true,
  showEmail: false,
  emailNotifications: true,
  showOnlineStatus: true,
  avatarUrl: ''
})

const rules = {
  required: v => !!v || t('validation.required'),
  maxLength: v => !v || v.length <= 500 || t('validation.maxLength', { max: 500 })
}

const previewAvatar = () => {
  if (avatarFile.value) {
    const file = Array.isArray(avatarFile.value) ? avatarFile.value[0] : avatarFile.value
    const reader = new FileReader()
    reader.onload = (e) => {
      avatarPreview.value = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

const removeAvatar = () => {
  avatarFile.value = null
  avatarPreview.value = null
  // TODO: API call to remove avatar from server
}

const saveProfile = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) return

  saving.value = true
  successMessage.value = ''
  errorMessage.value = ''

  try {
    // Upload avatar first if changed
    if (avatarFile.value) {
      const file = Array.isArray(avatarFile.value) ? avatarFile.value[0] : avatarFile.value
      const formData = new FormData()
      formData.append('avatar', file)

      const avatarResponse = await api.post('/profile/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (avatarResponse.data.success) {
        profileData.avatarUrl = avatarResponse.data.avatarUrl
      }
    }

    // Update profile
    const response = await api.put(`/profile/${profileData.userId}`, {
      displayName: profileData.displayName,
      bio: profileData.bio,
      location: profileData.location,
      website: profileData.website,
      birthday: profileData.birthday,
      interests: profileData.interests,
      showRealName: profileData.showRealName,
      showEmail: profileData.showEmail,
      emailNotifications: profileData.emailNotifications,
      showOnlineStatus: profileData.showOnlineStatus
    })

    if (response.data.success) {
      successMessage.value = t('profile.profileUpdated')
      avatarFile.value = null
      avatarPreview.value = null
      emit('updated')
    }
  } catch (error) {
    console.error('Error saving profile:', error)
    errorMessage.value = error.response?.data?.error || t('common.error')
  } finally {
    saving.value = false
  }
}

const resetForm = () => {
  loadProfileData()
  avatarFile.value = null
  avatarPreview.value = null
}

const loadProfileData = () => {
  if (props.profile) {
    Object.assign(profileData, {
      userId: props.profile.userId || 0,
      displayName: props.profile.displayName || '',
      username: props.profile.username || '',
      bio: props.profile.bio || '',
      location: props.profile.location || '',
      website: props.profile.website || '',
      birthday: props.profile.birthday || '',
      interests: props.profile.interests || '',
      showRealName: props.profile.showRealName === true,
      showEmail: props.profile.showEmail === true,
      emailNotifications: props.profile.emailNotifications === true,
      showOnlineStatus: props.profile.showOnlineStatus !== false, // Default to true
      avatarUrl: props.profile.avatarUrl || ''
    })
  }
}

watch(() => props.profile, loadProfileData, { immediate: true })
</script>

<style scoped>
</style>